/**
 * Panel event handlers — React wiring over the PURE floor-plan edit catalog
 * (Phase 3 of the state-management refactor): each handler resolves the
 * selection to a catalog target and REPLACES `state.shape`; nothing mutates the
 * room. Deletes don't confirm — undo restores.
 */

import {
  defaultShape,
  DoorSwing,
  EdgeSegmentType,
  ObjectSubtype,
  removeFreeElement,
  removeSegment,
  roomFloorType,
  rotateRoomQuarterTurns,
  sameSavedRoom,
  setFreeElementFloorType,
  setFreeElementObjectProps,
  setRoomPlacement,
  setSegmentOpeningHeight,
  setSegmentSwing,
  setSegmentType,
} from '@kaiizen/kai-floor-plan';
import { withPlacementOf } from '../assembler';
import { applyPanelInput, normalizeSelection, type PlanLayers, select, selectionSegmentRef, showsElement } from '../editor-model';
import { FloorPlanEditorState } from './FloorPlanEditorState';

export const noop = (): void => {};

/** Commit a number input (data-set key). Empty opening-height clears to "ceiling". */
export function commitPanelNumber(floorPlanEditorState: FloorPlanEditorState, setKey: string, rawText: string): void {
  if (setKey === 'opening-height' && rawText.trim() === '') {
    floorPlanEditorState.mutate(state => {
      const segmentReference = selectionSegmentRef(state.selection);
      if (segmentReference) state.shape = setSegmentOpeningHeight(state.shape, segmentReference.ref, segmentReference.si, null);
    });
    return;
  }
  const numericValue = parseFloat(rawText);
  if (Number.isNaN(numericValue)) return;
  floorPlanEditorState.mutate(state => applyPanelInput(state, setKey, numericValue, noop));
}

/**
 * Commit from the feet-and-inches widget: value is decimal feet, null means
 * both fields were blank (meaningful only for opening-height → "ceiling").
 */
export function commitPanelFeet(floorPlanEditorState: FloorPlanEditorState, setKey: string, valueFeet: number | null): void {
  commitPanelNumber(floorPlanEditorState, setKey, valueFeet == null ? '' : String(valueFeet));
}

/** al-kind select: change the segment's type; the selection follows the segment into its canonical form. */
export function setEdgeItemKind(floorPlanEditorState: FloorPlanEditorState, kind: EdgeSegmentType): void {
  floorPlanEditorState.mutate(state => {
    const segmentReference = selectionSegmentRef(state.selection);
    if (!segmentReference) return;
    state.shape = setSegmentType(state.shape, segmentReference.ref, segmentReference.si, kind);
    state.selection = normalizeSelection(state.shape, state.selection);
  });
}

/** al-swing select. */
export function setDoorSwing(floorPlanEditorState: FloorPlanEditorState, swing: DoorSwing): void {
  floorPlanEditorState.mutate(state => {
    const segmentReference = selectionSegmentRef(state.selection);
    if (segmentReference) state.shape = setSegmentSwing(state.shape, segmentReference.ref, segmentReference.si, swing);
  });
}

/**
 * Turn the whole room by quarter turns (positive is clockwise in plan) as one
 * undo step: the edges roll one notch per turn and width and length swap. A
 * room selection survives; any other selection named a wall, corner or
 * element that has now moved, so it clears. Read-only rooms stay as they are,
 * and so does a room mid-drag, whose commit would discard the turn.
 */
export function rotateRoom(floorPlanEditorState: FloorPlanEditorState, quarterTurns: number): void {
  floorPlanEditorState.mutate(state => {
    if (state.readOnly || state.drag || state.draftShape) return;
    state.shape = rotateRoomQuarterTurns(state.shape, quarterTurns);
    state.addMenu = null;
    state.editingKey = null;
    if (state.selection.kind !== 'room') select(state, { kind: 'none' });
  });
}

/** What shows inside the walls: hiding the selected element also unselects it. */
export function setPlanLayers(floorPlanEditorState: FloorPlanEditorState, layers: PlanLayers): void {
  floorPlanEditorState.mutate(state => {
    state.layers = layers;
    const { selection } = state;
    const selected = selection.kind === 'inner' || selection.kind === 'innerCorner' ? state.shape.freeElements?.[selection.index] : undefined;
    if (selected && !showsElement(layers, selected)) select(state, { kind: 'none' });
  });
}

/** inner-floortype select (flooring regions only). */
export function setInnerFloorType(floorPlanEditorState: FloorPlanEditorState, floorType: string): void {
  floorPlanEditorState.mutate(state => {
    const selection = state.selection;
    if (selection.kind !== 'inner' && selection.kind !== 'innerCorner') return;
    state.shape = setFreeElementFloorType(state.shape, selection.index, floorType);
  });
}

/**
 * Rename a flooring type everywhere it appears, as one commit (one undo step):
 * the room's own floor when it is that type (an unset floor, stored as
 * nothing or an empty string, is the "Default" row), and every flooring
 * region naming the type. Regions with no type of their own follow the
 * room's floor, so they are left as they are. Renaming onto a type already
 * present merges the two flooring-by-type rows, and an undefined `toKey`
 * unpicks the type, merging its row into the unset floor.
 */
export function replaceFloorTypeEverywhere(floorPlanEditorState: FloorPlanEditorState, fromKey: string, toKey: string | undefined): void {
  const room = floorPlanEditorState.stRef.current.shape;
  const freeElements = room.freeElements?.map(element =>
    element.customAreaType === 'flooring' && element.floorType === fromKey ? { ...element, floorType: toKey } : element
  );
  floorPlanEditorState.commit({ ...room, ...(roomFloorType(room) === fromKey && { floorType: toKey }), freeElements });
}

function setObjectProps(floorPlanEditorState: FloorPlanEditorState, props: { subtype?: ObjectSubtype; name?: string; notBuiltIn?: boolean }): void {
  floorPlanEditorState.mutate(state => {
    const selection = state.selection;
    if (selection.kind !== 'inner' && selection.kind !== 'innerCorner') return;
    state.shape = setFreeElementObjectProps(state.shape, selection.index, props);
  });
}

export const setInnerSubtype = (floorPlanEditorState: FloorPlanEditorState, subtype: ObjectSubtype): void => setObjectProps(floorPlanEditorState, { subtype });

export const setInnerName = (floorPlanEditorState: FloorPlanEditorState, name: string): void => setObjectProps(floorPlanEditorState, { name });

export const setInnerNotBuiltIn = (floorPlanEditorState: FloorPlanEditorState, notBuiltIn: boolean): void =>
  setObjectProps(floorPlanEditorState, { notBuiltIn });

/** data-act="del-alcove" — delete the selected edge item (no confirm; undo restores). */
export function deleteEdgeItem(floorPlanEditorState: FloorPlanEditorState): void {
  floorPlanEditorState.mutate(state => {
    const selection = state.selection;
    if (selection.kind !== 'alcove' && selection.kind !== 'alcoveCorner') return;
    state.shape = removeSegment(state.shape, { k: 'pe', edge: selection.edge }, selection.index);
    select(state, { kind: 'none' });
  });
}

/** data-act="del-lineseg" — delete the selected line segment (no confirm; undo restores). */
export function deleteLineSeg(floorPlanEditorState: FloorPlanEditorState): void {
  floorPlanEditorState.mutate(state => {
    const selection = state.selection;
    if (selection.kind !== 'lineSeg') return;
    state.shape = removeSegment(state.shape, selection.ref, selection.si);
    select(state, { kind: 'none' });
  });
}

/** data-act="del-inner" — delete the selected free element. */
export function deleteFreeElement(floorPlanEditorState: FloorPlanEditorState): void {
  floorPlanEditorState.mutate(state => {
    const selection = state.selection;
    if (selection.kind !== 'inner' && selection.kind !== 'innerCorner') return;
    state.shape = removeFreeElement(state.shape, selection.index);
    select(state, { kind: 'none' });
  });
}

/** Move the room to another building level as one undo step. Read-only rooms stay where they are. */
export function setRoomLevel(floorPlanEditorState: FloorPlanEditorState, level: number): void {
  floorPlanEditorState.mutate(state => {
    if (!state.readOnly) state.shape = setRoomPlacement(state.shape, { level });
  });
}

/**
 * Replace the room with the editor's default room as one undo step, keeping
 * the room's whole-property placement so it doesn't move in the floor plan
 * view. A room selection survives; any other selection named a part of the old
 * room, so it clears. Read-only rooms stay as they are, and so does a room
 * that already is the default room.
 */
export function resetRoom(floorPlanEditorState: FloorPlanEditorState): void {
  const { shape, readOnly } = floorPlanEditorState.stRef.current;
  const reset = withPlacementOf(defaultShape(), shape);
  if (readOnly || sameSavedRoom(reset, shape)) return;
  floorPlanEditorState.mutate(state => {
    state.shape = reset;
    state.draftShape = null;
    state.drag = null;
    state.addMenu = null;
    state.editingKey = null;
    if (state.selection.kind !== 'room') select(state, { kind: 'none' });
  });
}
